import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { getAdminToken } from "@/lib/adminApi";

interface AdminEditContextType {
  editMode: boolean;
  setEditMode: (on: boolean) => void;
  isAdmin: boolean;
}

const AdminEditContext = createContext<AdminEditContextType | undefined>(undefined);

export const AdminEditProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [editMode, setEditMode] = useState<boolean>(false);

  useEffect(() => {
    // Check for existing admin session
    const checkAdminStatus = () => {
      const token = getAdminToken();
      setIsAdmin(!!token);
    };

    // Initial check
    checkAdminStatus();
    
    // Listen for storage changes (when token is cleared/set)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "admin_token") {
        setIsAdmin(!!e.newValue);
        if (!e.newValue) setEditMode(false);
      }
    };

    // Listen for focus events to recheck admin status
    const handleFocus = () => {
      checkAdminStatus();
    };
    
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('focus', handleFocus);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  // Turn off edit if admin token disappears
  useEffect(() => {
    if (!isAdmin && editMode) setEditMode(false);
  }, [isAdmin, editMode]);

  const value = useMemo(() => ({ editMode, setEditMode, isAdmin }), [editMode, isAdmin]);
  return <AdminEditContext.Provider value={value}>{children}</AdminEditContext.Provider>;
};

export const useAdminEdit = () => {
  const ctx = useContext(AdminEditContext);
  if (!ctx) throw new Error("useAdminEdit must be used within AdminEditProvider");
  return ctx;
};
